import React, { lazy, ReactElement, Suspense, useMemo } from 'react'
import { useRemoteScript } from '../../hooks/remote-script'
import { GridLoading } from '../grid-loading'

export interface RemoteComponentProps {
  readonly src: string
  readonly scope: string
  readonly module: string
  readonly fallbackComponent?: ReactElement
  readonly [propName: string]: any
}

export const RemoteComponent = ({
  src,
  scope,
  module,
  fallbackComponent,
  ...props
}: RemoteComponentProps) => {
  const scriptStatus = useRemoteScript(src)

  const Component = lazy(async () => {
    if (!window[scope].initialised) {
      window[scope].init({
        react: {
          [React.version]: {
            get: () => Promise.resolve(() => React),
            loaded: true,
            from: 'contrast-ui'
          }
        }
      })
      window[scope].initialised = true
    }

    return (await window[scope].get(module))()
  })

  const ComponentMemo = useMemo(
    () => (scriptStatus !== 'LOADED' ? undefined : Component),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [scope, module, scriptStatus]
  ) as React.ElementType

  const loadingComponent = <GridLoading />

  if (scriptStatus === 'LOADING') {
    return fallbackComponent || loadingComponent
  }

  if (scriptStatus === 'ERROR') {
    throw Error(`Unable to load ${src}`)
  }

  return (
    <Suspense fallback={fallbackComponent || loadingComponent}>
      <ComponentMemo {...props} />
    </Suspense>
  )
}
